import { defineStore } from 'pinia'
import { ref } from 'vue'
import Api from '@/config/api.helper'
import qs from 'qs'

interface Filtros {
  de: string | null
  ate: string | null
  tipoFiltroData: string | null
  origem: string | string[] | null
  valorMin: number | null
  valorMax: number | null
  nomeDevedor: string | null
  regua_cobranca_ids?: number[] | null
  filtro_status?: string[] | null
}

interface Debito {
  id: string
  titulo: string
  parcela: string
  valor: number
  data_emissao: string
  data_vencimento: string
  observacao: string
  origem?: string
  created_at: string
}

interface Cliente {
  nome: string
  cpf: string
}

interface Inadimplencia {
  id: string
  uuid: string
  created_at: string
  debitos: Debito[]
  cliente: Cliente
  quantidade_debitos?: number
  valor_total?: number
}

interface InadimplenciaAgrupada {
  cpf: string
  tipo: string
  inadimplencia: Inadimplencia
}

interface InadimplenciaResponse {
  inadimplencias_agrupadas: InadimplenciaAgrupada[]
}

export const useInadimplenciaStore = defineStore('inadimplenciaStore', () => {
  const data = ref<InadimplenciaResponse | null>(null)
  const total = ref(0)
  const loading = ref(false)
  const expandedRows = ref<InadimplenciaAgrupada[]>([])
  const lastExpandedKeys = ref<Set<string>>(new Set())

  const page = ref(1)
  const perPage = ref(10)
  const sort = ref({ field: 'data_cadastro', order: 'desc' })

  const filtros = ref<Filtros>({
    de: null,
    ate: null,
    tipoFiltroData: null,
    origem: null,
    valorMin: null,
    valorMax: null,
    nomeDevedor: null,
    regua_cobranca_ids: null,
    filtro_status: null,
  })

  function setPage(newPage: number) {
    page.value = newPage
  }

  function setPerPage(newPerPage: number) {
    perPage.value = newPerPage
  }

  function setSort(field: string, order: string) {
    sort.value = { field, order }
  }

  function setFiltros(novosFiltros: Filtros) {
    const novosFiltrosProcessados: Record<string, any> = {}

    if (novosFiltros.de) novosFiltrosProcessados.de = novosFiltros.de
    if (novosFiltros.ate) novosFiltrosProcessados.ate = novosFiltros.ate
    if (novosFiltros.origem) novosFiltrosProcessados.origem = novosFiltros.origem
    if (novosFiltros.tipoFiltroData) novosFiltrosProcessados.tipoFiltroData = novosFiltros.tipoFiltroData
    if (typeof novosFiltros.valorMin === 'number') novosFiltrosProcessados.valorMin = Math.round(novosFiltros.valorMin * 100)
    if (typeof novosFiltros.valorMax === 'number') novosFiltrosProcessados.valorMax = Math.round(novosFiltros.valorMax * 100)
    if (novosFiltros.nomeDevedor) novosFiltrosProcessados.NomeDevedor = novosFiltros.nomeDevedor
    if (Array.isArray(novosFiltros.regua_cobranca_ids) && novosFiltros.regua_cobranca_ids.length > 0) {
      novosFiltrosProcessados.regua_cobranca_ids = novosFiltros.regua_cobranca_ids
    }
    if (Array.isArray(novosFiltros.filtro_status) && novosFiltros.filtro_status.length > 0) {
      novosFiltrosProcessados.filtro_status = novosFiltros.filtro_status
    }


    filtros.value = novosFiltrosProcessados
  }


  function saveExpandedState() {
    lastExpandedKeys.value = new Set(
      expandedRows.value.map(row => `${row.cpf}_${row.tipo}`),
    )
  }

  function restoreExpandedRows() {
    if (!data.value?.inadimplencias_agrupadas) return

    expandedRows.value = data.value.inadimplencias_agrupadas.filter(item =>
      lastExpandedKeys.value.has(`${item.cpf}_${item.tipo}`),
    )
  }

  async function fetchData() {
    loading.value = true
    try {
      const response = await Api.get<{ data: InadimplenciaResponse }>('v2/financeiro/inadimplencia', {
        params: {
          page: page.value,
          per_page: perPage.value,
          orderBy: sort.value.field,
          sortOrder: sort.value.order,
          ...filtros.value,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: 'brackets' })
        },
      })
      data.value = response.data.data
      total.value = data.value?.inadimplencias_agrupadas?.length || 0

      if (data.value?.inadimplencias_agrupadas) {
        restoreExpandedRows()
      }
    } catch (error) {
      console.error('Erro ao carregar dados de inadimplencia:', error)
    } finally {
      loading.value = false
    }
  }

 //console.log(expandedRows.value)

  return {
    data,
    total,
    page,
    perPage,
    sort,
    filtros,
    loading,
    expandedRows,
    lastExpandedKeys,

    fetchData,
    setPage,
    setPerPage,
    setSort,
    setFiltros,
    saveExpandedState,
    restoreExpandedRows,
  }
})
